import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiService } from '../../utils/apiService';
import { successToastConfig, errorToastConfig } from '../../utils/toastConfig';

const DoctorsSurveyForm = () => {
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        participation: 'anonymous', // Default
        practice_data: {
            name: '',
            contact_person: ''
        },
        q1: '',
        q2: '',
        q3: '',
        q4: '',
        q5: {
            a: '',
            b: '',
            c: '',
            d: ''
        },
        q6: [], // Multi-select
        q7: '',
        q8: '',
        q9: '',
        q9_other: '',
        q10: '',
        q10_details: [], // Multi-select
        q11: '',
        q12: '',
        q13: '',
        q14: '',
        q15: '',
        q16: '',
        contact: {
            person: '',
            details: ''
        }
    });

    // Helper to clear error for a specific field
    const clearError = (field) => {
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleParticipationChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, participation: value }));
        clearError('participation');
    };

    const handlePracticeDataChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            practice_data: { ...prev.practice_data, [name]: value }
        }));
        // We might want to validate this if participation is 'with_practice_data'
        if (formData.participation === 'with_practice_data') {
            clearError(`practice_data.${name}`);
        }
    };

    const handleSingleSelectChange = (questionId, value) => {
        setFormData(prev => ({ ...prev, [questionId]: value }));
        clearError(questionId);
    };

    const handleScaleChange = (subId, value) => {
        setFormData(prev => ({
            ...prev,
            q5: { ...prev.q5, [subId]: value }
        }));
        clearError(`q5.${subId}`);
    };

    const handleMultiSelectChange = (questionId, value) => {
        setFormData(prev => {
            const current = prev[questionId] || [];
            const updated = current.includes(value)
                ? current.filter(item => item !== value)
                : [...current, value];
            return { ...prev, [questionId]: updated };
        });
        // Manually clear error if at least one is selected
        clearError(questionId);
    };

    const handleTextChange = (e, qId, subField = null) => {
        const { value } = e.target;
        if (subField) {
            setFormData(prev => ({
                ...prev,
                [qId]: { ...prev[qId], [subField]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [qId]: value }));
        }
        // Manually clear error
        clearError(qId);
    };


    const validate = () => {
        const newErrors = {};

        // 1. Participation
        if (formData.participation === 'with_practice_data') {
            if (!formData.practice_data.name.trim()) newErrors['practice_data.name'] = t('survey.validation.required');
            if (!formData.practice_data.contact_person.trim()) newErrors['practice_data.contact_person'] = t('survey.validation.required');
        }

        // 2. Q1 - Single
        if (!formData.q1) newErrors.q1 = t('survey.validation.selectOne');
        // 3. Q2 - Single
        if (!formData.q2) newErrors.q2 = t('survey.validation.selectOne');
        // 4. Q3 - Single
        if (!formData.q3) newErrors.q3 = t('survey.validation.selectOne');
        // 5. Q4 - Single
        if (!formData.q4) newErrors.q4 = t('survey.validation.selectOne');

        // 6. Q5 - Scale a-d
        ['a', 'b', 'c', 'd'].forEach(sub => {
            if (!formData.q5[sub]) newErrors[`q5.${sub}`] = t('survey.validation.selectOne');
        });

        // 7. Q6 - Multi
        if (formData.q6.length === 0) newErrors.q6 = t('survey.validation.selectAtLeastOne');

        // 8. Q7 - Single
        if (!formData.q7) newErrors.q7 = t('survey.validation.selectOne');

        // 9. Q8 - Single
        if (!formData.q8) newErrors.q8 = t('survey.validation.selectOne');

        // 10. Q9 - Single + Optional 'Other'
        if (!formData.q9) newErrors.q9 = t('survey.validation.selectOne');
        if (formData.q9 === 'other' && !formData.q9_other.trim()) {
            newErrors.q9_other = t('survey.validation.enterText');
        }

        // 11. Q10 - Single + Conditional Multi
        if (!formData.q10) newErrors.q10 = t('survey.validation.selectOne');
        if (formData.q10 !== 'no' && formData.q10_details.length === 0) {
            // Only if user selected Yes or Partial, they MUST select at least one detail
            if (formData.q10) newErrors.q10_details = t('survey.validation.selectAtLeastOne');
        }

        // 12. Q11 - Single
        if (!formData.q11) newErrors.q11 = t('survey.validation.selectOne');

        // 13. Q12 - Single
        if (!formData.q12) newErrors.q12 = t('survey.validation.selectOne');

        // 14. Q13 - Single
        if (!formData.q13) newErrors.q13 = t('survey.validation.selectOne');

        // 15. Q14 - Single
        if (!formData.q14) newErrors.q14 = t('survey.validation.selectOne');

        // 16. Q15 - Single (Optional but if configured as required in plan it is not explicitly optional except by logic 'skip', 
        // actually Plan says Q15, Q16, Q17 optional in plan, but Q15 has a 'Skip' option so we enforce selection)
        if (!formData.q15) newErrors.q15 = t('survey.validation.selectOne');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (validate()) {
            try {
                await apiService.postDoctorsSurvey(formData);
                toast.success(t('survey.submit.success'), successToastConfig);
                // Reset or redirect could happen here
                // For now, maybe just Scroll to top
                window.scrollTo(0, 0);
                // Reset form functionality if needed
            } catch (err) {
                console.error(err);
                toast.error(t('survey.submit.error'), errorToastConfig);
            }
        } else {
            toast.error(t('survey.submit.error'), errorToastConfig);
            // Find first error and scroll to it
            const firstError = document.querySelector('.error-text');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
        setLoading(false);
    };

    // --- Render Helpers ---

    const renderSingleSelect = (qId, options) => (
        <div className={`survey-question ${errors[qId] ? 'has-error' : ''}`}>
            <h3>{t(`survey.questions.${qId}.label`)}</h3>
            <div className="options-grid">
                {Object.entries(options).map(([key, label]) => (
                    <label key={key} className={`option-card ${formData[qId] === key ? 'selected' : ''}`}>
                        <input
                            type="radio"
                            name={qId}
                            value={key}
                            checked={formData[qId] === key}
                            onChange={() => handleSingleSelectChange(qId, key)}
                        />
                        <span className="custom-radio"></span>
                        <span className="label-text">{label}</span>
                    </label>
                ))}
            </div>
            {errors[qId] && <div className="error-text">{errors[qId]}</div>}
        </div>
    );

    return (
        <form className="doctors-survey-form" onSubmit={handleSubmit}>

            {/* Participation */}
            <div className="survey-section participation-section">
                <h3>{t('survey.participation.label')}</h3>
                <div className="options-grid">
                    {['anonymous', 'with_practice_data'].map(mode => (
                        <label key={mode} className={`option-card ${formData.participation === mode ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="participation"
                                value={mode}
                                checked={formData.participation === mode}
                                onChange={handleParticipationChange}
                            />
                            <span className="custom-radio"></span>
                            <span className="label-text">
                                {mode === 'anonymous' ? t('survey.participation.anonymous') : t('survey.participation.withPracticeData')}
                            </span>
                        </label>
                    ))}
                </div>

                {formData.participation === 'with_practice_data' && (
                    <div className="practice-details fade-in">
                        <div className="st-form-field">
                            <input
                                type="text"
                                name="name"
                                placeholder={t('survey.participation.practiceName')}
                                value={formData.practice_data.name}
                                onChange={handlePracticeDataChange}
                                className={errors['practice_data.name'] ? 'error-border' : ''}
                            />
                            {errors['practice_data.name'] && <div className="error-text">{errors['practice_data.name']}</div>}
                        </div>
                        <div className="st-form-field">
                            <input
                                type="text"
                                name="contact_person"
                                placeholder={t('survey.participation.contactPerson')}
                                value={formData.practice_data.contact_person}
                                onChange={handlePracticeDataChange}
                                className={errors['practice_data.contact_person'] ? 'error-border' : ''}
                            />
                            {errors['practice_data.contact_person'] && <div className="error-text">{errors['practice_data.contact_person']}</div>}
                        </div>
                    </div>
                )}
            </div>

            {renderSingleSelect('q1', t('survey.questions.q1.options'))}
            {renderSingleSelect('q2', t('survey.questions.q2.options'))}
            {renderSingleSelect('q3', t('survey.questions.q3.options'))}
            {renderSingleSelect('q4', t('survey.questions.q4.options'))}

            {/* Q5 Scale */}
            <div className="survey-question">
                <h3>{t('survey.questions.q5.label')}</h3>
                <div className="scale-container">
                    {['a', 'b', 'c', 'd'].map(sub => (
                        <div key={sub} className="scale-row">
                            <span className="scale-label">{t(`survey.questions.q5.subLabels.${sub}`)}</span>
                            <div className="scale-wrapper">
                                <div className="scale-legends">
                                    <span className="scale-legend-text">{t('survey.questions.q5.scale.low')}</span>
                                    <span className="scale-legend-text">{t('survey.questions.q5.scale.high')}</span>
                                </div>
                                <div className="scale-options">
                                    {[1, 2, 3, 4, 5].map(num => (
                                        <label key={num} className={`scale-btn ${formData.q5[sub] === num ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name={`q5-${sub}`}
                                                value={num}
                                                checked={formData.q5[sub] === num}
                                                onChange={() => handleScaleChange(sub, num)}
                                            />
                                            {num}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {errors[`q5.${sub}`] && <div className="error-text">{errors[`q5.${sub}`]}</div>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Q6 Multi */}
            <div className={`survey-question ${errors.q6 ? 'has-error' : ''}`}>
                <h3>{t('survey.questions.q6.label')}</h3>
                <div className="options-grid">
                    {Object.entries(t('survey.questions.q6.options')).map(([key, label]) => (
                        <label key={key} className={`option-card ${formData.q6.includes(key) ? 'selected' : ''}`}>
                            <input
                                type="checkbox"
                                value={key}
                                checked={formData.q6.includes(key)}
                                onChange={() => handleMultiSelectChange('q6', key)}
                            />
                            <span className="custom-checkbox"></span>
                            <span className="label-text">{label}</span>
                        </label>
                    ))}
                </div>
                {errors.q6 && <div className="error-text">{errors.q6}</div>}
            </div>

            {renderSingleSelect('q7', t('survey.questions.q7.options'))}
            {renderSingleSelect('q8', t('survey.questions.q8.options'))}

            {/* Q9 with Other */}
            <div className={`survey-question ${errors.q9 ? 'has-error' : ''}`}>
                <h3>{t('survey.questions.q9.label')}</h3>
                <div className="options-grid">
                    {Object.entries(t('survey.questions.q9.options')).map(([key, label]) => (
                        <label key={key} className={`option-card ${formData.q9 === key ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="q9"
                                value={key}
                                checked={formData.q9 === key}
                                onChange={() => handleSingleSelectChange('q9', key)}
                            />
                            <span className="custom-radio"></span>
                            <span className="label-text">{label}</span>
                        </label>
                    ))}
                </div>
                {formData.q9 === 'other' && (
                    <div className="other-input fade-in">
                        <input
                            type="text"
                            placeholder={t('survey.questions.q9.otherPlaceholder')}
                            value={formData.q9_other}
                            onChange={(e) => handleTextChange(e, 'q9_other')}
                            className={errors.q9_other ? 'error-border' : ''}
                        />
                        {errors.q9_other && <div className="error-text">{errors.q9_other}</div>}
                    </div>
                )}
                {errors.q9 && <div className="error-text">{errors.q9}</div>}
            </div>

            {/* Q10 with Follow up */}
            <div className={`survey-question ${errors.q10 ? 'has-error' : ''}`}>
                <h3>{t('survey.questions.q10.label')}</h3>
                <div className="options-grid">
                    {Object.entries(t('survey.questions.q10.options')).map(([key, label]) => (
                        <label key={key} className={`option-card ${formData.q10 === key ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="q10"
                                value={key}
                                checked={formData.q10 === key}
                                onChange={() => handleSingleSelectChange('q10', key)}
                            />
                            <span className="custom-radio"></span>
                            <span className="label-text">{label}</span>
                        </label>
                    ))}
                </div>
                {errors.q10 && <div className="error-text">{errors.q10}</div>}

                {(formData.q10 === 'yes' || formData.q10 === 'partial') && (
                    <div className="follow-up-section fade-in">
                        <h4>{t('survey.questions.q10.followUp.label')}</h4>
                        <div className="options-grid">
                            {Object.entries(t('survey.questions.q10.followUp.options')).map(([key, label]) => (
                                <label key={key} className={`option-card ${formData.q10_details.includes(key) ? 'selected' : ''}`}>
                                    <input
                                        type="checkbox"
                                        value={key}
                                        checked={formData.q10_details.includes(key)}
                                        onChange={() => handleMultiSelectChange('q10_details', key)}
                                    />
                                    <span className="custom-checkbox"></span>
                                    <span className="label-text">{label}</span>
                                </label>
                            ))}
                        </div>
                        {errors.q10_details && <div className="error-text">{errors.q10_details}</div>}
                    </div>
                )}
            </div>

            {renderSingleSelect('q11', t('survey.questions.q11.options'))}
            {renderSingleSelect('q12', t('survey.questions.q12.options'))}
            {renderSingleSelect('q13', t('survey.questions.q13.options'))}
            {renderSingleSelect('q14', t('survey.questions.q14.options'))}
            {renderSingleSelect('q15', t('survey.questions.q15.options'))}

            {/* Q16 Text */}
            <div className="survey-question">
                <h3>{t('survey.questions.q16.label')}</h3>
                <div className="st-form-field">
                    <textarea
                        placeholder={t('survey.questions.q16.placeholder')}
                        value={formData.q16}
                        onChange={(e) => handleTextChange(e, 'q16')}
                    />
                </div>
            </div>

            {/* Q17 Contact */}
            <div className="survey-section contact-section">
                <h3>{t('survey.questions.q17.label')}</h3>
                <p>{t('survey.questions.q17.description')}</p>
                <div className="row">
                    <div className="col-md-6">
                        <div className="st-form-field">
                            <input
                                type="text"
                                placeholder={t('survey.questions.q17.personPlaceholder')}
                                value={formData.contact.person}
                                onChange={(e) => handleTextChange(e, 'contact', 'person')}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="st-form-field">
                            <input
                                type="text"
                                placeholder={t('survey.questions.q17.detailsPlaceholder')}
                                value={formData.contact.details}
                                onChange={(e) => handleTextChange(e, 'contact', 'details')}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-submit-wrapper">
                <button
                    type="submit"
                    className="st-btn st-style1 st-color1 st-size-medium"
                    disabled={loading}
                >
                    {loading ? t('survey.submit.sending') : t('survey.submit.button')}
                </button>
            </div>

        </form>
    );
};

export default DoctorsSurveyForm;
